import { useEffect, useMemo, useRef, useState } from "react";
import {
  loadDocumentLibraryFilterState,
} from "../utils/documentLibraryFilterPersistence";
import {
  sortConfigToDocumentLibraryApiParams,
} from "../utils/documentLibrarySortMapper";
import usePersistDocumentLibraryFilters from "./usePersistDocumentLibraryFilters";

/**
 * Shared sticky-filter state for document-library screens (RFI / VQA / etc.).
 * Hydrates from sessionStorage, persists changes, and resets page on filter/search
 * changes while preserving the hydrated page on first mount.
 */
export default function useDocumentLibraryStickyFilters(screenKey, searchQuery) {
  const initialFilterState = useMemo(
    () => loadDocumentLibraryFilterState(screenKey),
    [screenKey]
  );

  const [currentPage, setCurrentPage] = useState(initialFilterState.currentPage);
  const [selectedIds, setSelectedIds] = useState(initialFilterState.selectedIds);
  const [sortConfig, setSortConfig] = useState(initialFilterState.sortConfig);
  const [sortFetchNonce, setSortFetchNonce] = useState(0);

  const serverSortParams = useMemo(
    () => sortConfigToDocumentLibraryApiParams(sortConfig),
    [sortConfig]
  );

  const handleSort = (key) => {
    setSortConfig((prevConfig) => {
      const newDirection =
        prevConfig[key]?.direction === "asc" ? "desc" : "asc";
      return { [key]: { direction: newDirection } };
    });
    setSortFetchNonce((n) => n + 1);
    setCurrentPage(1);
  };

  const persistedFilterState = useMemo(
    () => ({
      selectedIds,
      sortConfig,
      currentPage,
    }),
    [selectedIds, sortConfig, currentPage]
  );

  usePersistDocumentLibraryFilters(screenKey, persistedFilterState);

  const skipPageResetOnMount = useRef(true);

  useEffect(() => {
    if (skipPageResetOnMount.current) {
      skipPageResetOnMount.current = false;
      return;
    }

    setCurrentPage(1);
  }, [selectedIds, searchQuery]);

  return {
    selectedIds,
    setSelectedIds,
    sortConfig,
    handleSort,
    currentPage,
    setCurrentPage,
    serverSortParams,
    sortFetchNonce,
  };
}
